import React, { useContext, useEffect, useState } from 'react'
import { displayNGNCurrency } from '../../helpers/displayCurrency';
import { useTheme } from '../Theme/ThemeProvider';
import Context from '../../context';
import addToCart from '../../helpers/addToCart';
import { ShoppingCart, Star } from 'lucide-react';
import { summaryApi } from '../../common';
import { Link } from 'react-router-dom';

function ViewSearch({ data }) {
    const { colors } = useTheme();
    const { fetchUserAddToCart } = useContext(Context);
    const [productRatings, setProductRatings] = useState({});

    const handleAddToCart = (e, productId) => {
        addToCart(e, productId, fetchUserAddToCart);
    };

    const fetchProductRating = async (productId) => {
        try {
            const response = await fetch(`${summaryApi.getReviews.url}/${productId}`, {
                method: summaryApi.getReviews.method,
                credentials: 'include'
            });
            const reviewData = await response.json();
            
            if (reviewData.data && reviewData.data.length > 0) {
                const totalRating = reviewData.data.reduce((sum, review) => sum + review.rating, 0);
                const averageRating = totalRating / reviewData.data.length;
                
                setProductRatings(prev => ({
                    ...prev,
                    [productId]: {
                        average: averageRating,
                        count: reviewData.data.length
                    }
                }));
            } else {
                setProductRatings(prev => ({
                    ...prev,
                    [productId]: {
                        average: 0,
                        count: 0
                    }
                }));
            }
        } catch (error) {
            console.error(`Error fetching rating for product ${productId}:`, error);
        }
    };

    useEffect(() => {
        if (data && data.length > 0) {
            data.forEach(product => {
                fetchProductRating(product._id);
            });
        }
    }, [data]);

    const renderStars = (rating) => {
        return [...Array(5)].map((_, i) => (
            <Star
                key={i}
                size={14}
                fill={i < Math.floor(rating) ? colors.primary : i < rating ? colors.primary : colors.border}
                style={{ color: i < Math.floor(rating) ? colors.primary : i < rating ? colors.primary : colors.border }}
            />
        ));
    };

    if (!data || data.length === 0) {
        return (
            <div className="flex justify-center items-center py-12">
                <p style={{ color: colors.textSecondary }} className="text-lg">
                    No products found
                </p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
            {data.map((product) => (
                <div 
                    key={product._id} 
                    className="rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:scale-105 group"
                    style={{
                        backgroundColor: colors.bg,
                        border: `1px solid ${colors.border}`,
                    }}
                >
                    {/* Image Section */}
                    <div className="relative overflow-hidden h-48"
                        style={{ backgroundColor: colors.bgSecondary }}
                    >
                        <Link to={`/product/${product._id}`}>
                            <img 
                                src={product.productImage[0]} 
                                alt={product.productName} 
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300 cursor-pointer"
                            />
                        </Link>
                        
                        {/* Discount Badge */}
                        {product.productDiscount && product.productPrice && (
                            <div 
                                className="absolute top-3 right-3 px-3 py-1 rounded-full text-white text-xs font-bold"
                                style={{ backgroundColor: colors.accent }}
                            >
                                -{Math.round((product.productDiscount / product.productPrice) * 100)}%
                            </div>
                        )}

                        {/* Category Badge */}
                        <div 
                            className="absolute top-3 left-3 px-3 py-1 rounded-full text-white text-xs font-semibold"
                            style={{ backgroundColor: colors.primary }}
                        >
                            {product.productsCategory}
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-4 flex flex-col h-full">
                        {/* Product Name */}
                        <h3 
                            className="text-lg font-bold mb-2 line-clamp-2"
                            style={{ color: colors.text }}
                        >
                            {product.productName}
                        </h3>

                        {/* Brand */}
                        <p 
                            className="text-xs font-medium mb-2 uppercase tracking-wide"
                            style={{ color: colors.primary }}
                        >
                            {product.productBrand}
                        </p>

                        {/* Description */}
                        <p 
                            className="text-sm mb-3 line-clamp-2"
                            style={{ color: colors.textSecondary }}
                        >
                            {product.productDescription}
                        </p>

                        {/* Rating Section */}
                        <div className="flex items-center gap-2 mb-3">
                            <div className="flex gap-0.5">
                                {productRatings[product._id] ? (
                                    renderStars(productRatings[product._id].average)
                                ) : (
                                    [...Array(5)].map((_, i) => (
                                        <Star 
                                            key={i}
                                            size={14}
                                            style={{ color: colors.border }}
                                        />
                                    ))
                                )}
                            </div>
                            <p 
                                className="text-xs"
                                style={{ color: colors.textSecondary }}
                            >
                                {productRatings[product._id] ? (
                                    productRatings[product._id].count > 0 ? (
                                        `${productRatings[product._id].average.toFixed(1)}/5 (${productRatings[product._id].count})`
                                    ) : (
                                        'No reviews'
                                    )
                                ) : (
                                    'Loading...'
                                )}
                            </p>
                        </div>

                        {/* Footer Section */}
                        <div className="mt-auto pt-3 border-t space-y-3"
                            style={{ borderColor: colors.border }}
                        >
                            {/* Price */}
                            <div>
                                <span 
                                    className="text-xl font-bold"
                                    style={{ color: colors.primary }}
                                >
                                    {displayNGNCurrency(product.productPrice)}
                                </span>
                                {product.productDiscount && (
                                    <p 
                                        className="text-xs line-through"
                                        style={{ color: colors.textSecondary }}
                                    >
                                        {displayNGNCurrency(
                                            product.productPrice + product.productDiscount
                                        )}
                                    </p>
                                )}
                            </div>

                            {/* Add to Cart Button - Full Width */}
                            <button
                                onClick={(e) => handleAddToCart(e, product._id)} 
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-white transition-all hover:scale-105 active:scale-95"
                                style={{ backgroundColor: colors.primary }}
                            >
                                <ShoppingCart size={18} />
                                <span>Add to Cart</span>
                            </button>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default ViewSearch;